//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import { PojoSet } from 'pojo-sets';
import { DateUtils } from 'unison';

import { EngagementPostType } from '@apis/instagram-user/instagramUserApiModel';
import { InstagramAccountEngagementPostsActions } from '@instagram-account/redux/engagement/posts/instagramAccountEngagementPostsActions';
import {
  EngagementPostsTrendGraphValue,
  EngagementPostView,
  IInstagramAccountEngagementPostsState,
  INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS,
} from '@instagram-account/redux/engagement/posts/instagramAccountEngagementPostsModel';
import { UniversalSearchSortDirection } from '@models/UniversalSearchSortDirection';
import { UniversalSearchSortKey } from '@models/UniversalSearchSortKey';

const initialState: IInstagramAccountEngagementPostsState = {
  dateRange: DateUtils.getDateRangeFromAlias('last7Days'),
  flags: {
    isFetchingPosts: false,
    isFetchingPostsStats: false,
    isFetchingRecentPosts: false,
    isFetchingLivePosts: false,
    isFetchingMentionedTaggedPosts: false,
    isFetchingLabels: false,
    hasFetchedPostsStats: false,
    hasFetchedPosts: false,
    isFetchingCommentsExport: false,
    isFetchingPostPerformanceExport: false,
    isFetchingExportPostPerformanceToEmail: false,
  },
  posts: [],
  postType: EngagementPostType.All,
  totalPostCount: 0,

  labels: [],
  selectedLabels: PojoSet.empty(),

  postViewType: EngagementPostView.Table,

  selectedTrendGraphValue: EngagementPostsTrendGraphValue.TotalEngagementRate,
  aggregateStats: undefined,
  aggregateComparisonStats: undefined,
  trendStats: undefined,

  limit: 8,
  offset: 0,
  sortDir: UniversalSearchSortDirection.Desc,
  sortKey: UniversalSearchSortKey.PostedTimestamp,

  livePosts: [],
  fetchLivePostsError: undefined,

  recentPosts: [],
  mentionedTaggedPosts: [],

  fetchCommentsExportError: undefined,
  fetchPostPerformanceExportError: undefined,
  fetchExportPostPerformanceToEmailError: undefined,
};

export default function InstagramAccountEngagementPostsReducer(
  state: IInstagramAccountEngagementPostsState = initialState,
  action: InstagramAccountEngagementPostsActions,
): IInstagramAccountEngagementPostsState {
  switch (action.type) {
    case INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.SET_DATE_RANGE: {
      return {
        ...state,
        dateRange: action.dateRange,
      };
    }
    case INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.SET_POST_TYPE: {
      return {
        ...state,
        postType: action.postType,
      };
    }
    case INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.SET_SELECTED_TREND_GRAPH_VALUE: {
      return {
        ...state,
        selectedTrendGraphValue: action.selectedTrendGraphValue,
      };
    }
    case INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.SET_POST_VIEW_TYPE: {
      return {
        ...state,
        postViewType: action.postViewType,
      };
    }
    case INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.SET_PAGE_PARAMETERS: {
      return {
        ...state,
        ...action.params,
      };
    }
    case INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.SET_SELECTED_LABELS: {
      return {
        ...state,
        selectedLabels: action.labels,
      };
    }
    case INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.REQUEST_LABELS: {
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingLabels: true,
        },
        fetchLabelsError: initialState.fetchLabelsError,
      };
    }
    case INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.REQUEST_LABELS_SUCCEEDED: {
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingLabels: false,
        },
        labels: action.labels,
      };
    }
    case INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.REQUEST_LABELS_FAILED: {
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingLabels: false,
        },
        fetchLabelsError: action.message,
      };
    }
    case INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.REQUEST_POSTS:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingPosts: true,
        },
      };
    case INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.RECEIVE_POSTS_SUCCEEDED:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingPosts: false,
          hasFetchedPosts: true,
        },
        posts: action.posts,
        totalPostCount: action.totalPostCount,
      };
    case INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.RECEIVE_POSTS_FAILED:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingPosts: false,
        },
        posts: initialState.posts,
        totalPostCount: initialState.totalPostCount,
      };
    case INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.REQUEST_POSTS_STATS:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingPostsStats: true,
        },
      };
    case INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.RECEIVE_POSTS_STATS_SUCCEEDED:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingPostsStats: false,
          hasFetchedPostsStats: true,
        },
        aggregateStats: action.aggregateStats,
        aggregateComparisonStats: action.aggregateComparisonStats,
        trendStats: action.trendStats,
      };
    case INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.RECEIVE_POSTS_STATS_FAILED:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingPostsStats: false,
        },
      };
    case INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.REQUEST_RECENT_POSTS:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingRecentPosts: true,
        },
      };
    case INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.RECEIVE_RECENT_POSTS_SUCCEEDED:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingRecentPosts: false,
        },
        recentPosts: action.recentPosts,
      };
    case INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.RECEIVE_RECENT_POSTS_FAILED:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingRecentPosts: false,
        },
        recentPosts: initialState.recentPosts,
      };
    case INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.REQUEST_LIVE_POSTS:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingLivePosts: true,
        },
        fetchLivePostsError: initialState.fetchLivePostsError,
      };
    case INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.RECEIVE_LIVE_POSTS_SUCCEEDED:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingRecentPosts: false,
        },
        livePosts: action.livePosts,
      };
    case INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.RECEIVE_LIVE_POSTS_FAILED:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingLivePosts: false,
        },
        fetchLivePostsError: action.message,
        livePosts: initialState.livePosts,
      };
    case INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.REQUEST_MENTIONED_POSTS:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingMentionedTaggedPosts: true,
        },
      };
    case INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.RECEIVE_MENTIONED_POSTS_SUCCEEDED:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingMentionedTaggedPosts: false,
        },
        mentionedTaggedPosts: action.mentionedTaggedPosts,
      };
    case INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.RECEIVE_MENTIONED_POSTS_FAILED:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingMentionedTaggedPosts: false,
        },
        mentionedTaggedPosts: initialState.mentionedTaggedPosts,
      };
    case INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.REQUEST_COMMENTS_EXPORT:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingCommentsExport: true,
        },
        fetchCommentsExportError: undefined,
      };
    case INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.RECEIVE_COMMENTS_EXPORT_SUCCEEDED:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingCommentsExport: false,
        },
      };
    case INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.RECEIVE_COMMENTS_EXPORT_FAILED:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingCommentsExport: false,
        },
        fetchCommentsExportError: action.message,
      };
    case INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.REQUEST_EXPORT_POST_PERFORMANCE_TO_EMAIL:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingExportPostPerformanceToEmail: true,
        },
        fetchExportPostPerformanceToEmailError: undefined,
      };
    case INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.RECEIVE_EXPORT_POST_PERFORMANCE_TO_EMAIL_SUCCEEDED:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingExportPostPerformanceToEmail: false,
        },
      };
    case INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.RECEIVE_EXPORT_POST_PERFORMANCE_TO_EMAIL_FAILED:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingExportPostPerformanceToEmail: false,
        },
        fetchExportPostPerformanceToEmailError: action.message,
      };
    case INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.REQUEST_POST_PERFORMANCE_EXPORT:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingPostPerformanceExport: true,
        },
        fetchPostPerformanceExportError: undefined,
      };
    case INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.RECEIVE_POST_PERFORMANCE_EXPORT_SUCCEEDED:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingPostPerformanceExport: false,
        },
        fetchPostPerformanceExportError: undefined,
      };
    case INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.RECEIVE_POST_PERFORMANCE_EXPORT_FAILED:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingPostPerformanceExport: false,
        },
        fetchPostPerformanceExportError: action.message,
      };
    default:
      return state;
  }
}
